import {
  QuestionnaireCategoryItemsItemOneOf as Question,
  QuestionDecimal,
  QuestionHeight,
  QuestionMultiSelect,
  QuestionNameEmail,
  Questionnaire,
  QuestionnaireCategory,
  QuestionnaireCategoryItemsItem,
  QuestionnaireCategoryItemsItemOneOfTwo,
  QuestionNumeric,
  QuestionNumericStepper,
  QuestionRadio,
  QuestionSelect,
  QuestionWeight,
  ValidationsDate,
  ValidationsDecimal,
  ValidationsHeight,
  ValidationsNumeric,
  ValidationsText,
  ValidationsWeight,
} from '@/api/generated-catch-backend/model';
import { FieldErrors } from 'react-hook-form';
import { z } from 'zod';
import { Interstitial } from '../components/types';
import { HACK_birthdayValidation, HACK_defaultBirthday, QUESTION_SLUG_BIRTHDAY } from '../hooks/hack-questionnaire';


/**
 * Utility type for inferring the complete form data structure
 */
export type QuestionnaireFormData = Record<
  string,
  | string
  | number
  | boolean
  | null
  | string[]
  | Record<string, string>
  | Record<string, string | number>
  | Record<string, Record<string, number>>
>;

/**
 * Activity value structure for exercise-activity-matrix questions
 */
export const ActivityValueSchema = z.object({
  daysPerWeek: z.number().min(0).max(7),
  minutesPerDay: z.number().min(0).max(1440), // max minutes in a day
});

/**
 * Height/Weight value structure
 */
export const HeightWeightSchema = z.object({
  weight: z.string().or(z.number()),
  height: z.string().or(z.number()),
});

/**
 * Name/Email value structure
 */
export const NameEmailSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Please enter a valid email address'),
});

/**
 * Creates a Zod schema for a single question based on its type and validation rules
 */
export function createQuestionSchema(question: Question): z.ZodSchema {
  const { questionType, required, validations } = question;

  let baseSchema: z.ZodSchema;

  switch (questionType) {
    case 'text': {
      let textSchema = z.string();
      const textValidations = validations as ValidationsText;

      // Apply min/max length validations
      if (textValidations?.minLength && textValidations.minLength > 0) {
        textSchema = textSchema.min(
          textValidations.minLength as number,
          `Minimum ${textValidations.minLength} characters required`,
        );
      }
      if (textValidations?.maxLength && textValidations.maxLength > 0) {
        textSchema = textSchema.max(
          textValidations.maxLength as number,
          `Maximum ${textValidations.maxLength} characters allowed`,
        );
      }

      // Apply pattern validation
      if (textValidations?.pattern && typeof textValidations.pattern === 'string') {
        textSchema = textSchema.regex(new RegExp(textValidations.pattern), 'Invalid format');
      }
      if (!required) {
        baseSchema = z.union([textSchema, z.literal(''), z.undefined()]);
      } else {
        baseSchema = textSchema;
      }
      break;
    }

    case 'number':
    case 'number-stepper': {
      const numericValidations = validations as ValidationsNumeric;

      let numberSchema = z.number({
        required_error: 'Please enter a valid number',
        invalid_type_error: 'Please enter a valid number',
      });

      // Apply min/max validations
      if (numericValidations?.min !== undefined && numericValidations?.min !== null) {
        numberSchema = numberSchema.min(numericValidations.min as number, `Minimum value is ${numericValidations.min}`);
      }
      if (numericValidations?.max !== undefined && numericValidations?.max !== null) {
        numberSchema = numberSchema.max(numericValidations.max as number, `Maximum value is ${numericValidations.max}`);
      }

      if (!required) {
        baseSchema = z.union([numberSchema, z.undefined()]);
      } else {
        baseSchema = numberSchema;
      }
      break;
    }

    case 'decimal': {
      let decimalSchema = z.number({
        required_error: 'Please enter a valid decimal number',
        invalid_type_error: 'Please enter a valid decimal number',
      });

      const decimalValidations = validations as ValidationsDecimal;

      // Apply min/max validations
      if (decimalValidations?.min !== undefined && decimalValidations?.min !== null) {
        decimalSchema = decimalSchema.min(
          decimalValidations.min as number,
          `Minimum value is ${decimalValidations.min}`,
        );
      }
      if (decimalValidations?.max !== undefined && decimalValidations?.max !== null) {
        decimalSchema = decimalSchema.max(
          decimalValidations.max as number,
          `Maximum value is ${decimalValidations.max}`,
        );
      }

      // Add decimal places validation through refinement
      let finalDecimalSchema: z.ZodSchema = decimalSchema;
      if (decimalValidations?.decimalPlaces !== undefined && decimalValidations.decimalPlaces !== null) {
        const decimalPlaces = decimalValidations.decimalPlaces;
        finalDecimalSchema = decimalSchema.refine(
          (val) => {
            const decimalCount = val.toString().split('.')[1]?.length || 0;
            return decimalCount <= decimalPlaces;
          },
          { message: `Maximum ${decimalValidations.decimalPlaces} decimal places allowed` },
        );
      }

      if (!required) {
        baseSchema = z.union([finalDecimalSchema, z.undefined()]);
      } else {
        baseSchema = finalDecimalSchema;
      }
      break;
    }

    case 'date': {
      let dateSchema: z.ZodSchema = z.string().refine(
        (val) => {
          const date = new Date(val);
          return !isNaN(date.getTime());
        },
        { message: 'Please enter a valid date' },
      );

      let dateValidations = validations as ValidationsDate;

      if (question.slug === QUESTION_SLUG_BIRTHDAY) {
        dateValidations = HACK_birthdayValidation();
      }

      // Apply date range validations
      if (dateValidations?.minDate) {
        const minDate = new Date(dateValidations.minDate as string);
        dateSchema = dateSchema.refine(
          (val: string) => {
            if (!val) return true;
            const date = new Date(val);
            return date >= minDate;
          },
          { message: `Date must be after ${minDate.toLocaleDateString()}` },
        );
      }

      if (dateValidations?.maxDate) {
        const maxDate = new Date(dateValidations.maxDate as string);
        dateSchema = dateSchema.refine(
          (val) => {
            if (!val) return true;
            const date = new Date(val);
            return date <= maxDate;
          },
          { message: `Date must be before ${maxDate.toLocaleDateString()}` },
        );
      }

      if (!required) {
        baseSchema = z.union([dateSchema, z.literal(''), z.undefined()]);
      } else {
        baseSchema = dateSchema;
      }
      break;
    }

    case 'select':
    case 'radio': {
      const selectQuestion = question as QuestionSelect | QuestionRadio;
      if (!selectQuestion.options || selectQuestion.options.length === 0) {
        // Create default options if none exist
        baseSchema = z.enum(['yes', 'no'], {
          errorMap: (issue, ctx) => ({ message: 'Please select an option' }),
        });
      } else {
        // Create enum from option slugs
        const optionSlugs = selectQuestion.options.map((opt) => opt.slug) as [string, ...string[]];
        baseSchema = z.enum(optionSlugs, {
          errorMap: (issue, ctx) => ({ message: 'Please select an option' }),
        });
      }
      // zod enums, don't use
      break;
    }

    case 'multiselect': {
      const multiselectQuestion = question as QuestionMultiSelect;
      if (!multiselectQuestion.options || multiselectQuestion.options.length === 0) {
        // Create default options if none exist
        baseSchema = z.array(z.enum(['yes', 'no'])).default([]);
      } else {
        // Create array of enum values
        const optionSlugs = multiselectQuestion.options.map((opt) => opt.slug) as [string, ...string[]];
        baseSchema = z
          .array(z.enum(optionSlugs), {
            errorMap: (issue, ctx) => ({ message: 'Please select at least one option' }),
          })
          .min(1, 'Please select at least one option')
          .default([]);
      }
      break;
    }
    case 'height': {
      let heightSchema = z.number({
        required_error: 'Please enter a valid height',
        invalid_type_error: 'Please enter a valid height',
      });
      const heightQuestion = question as QuestionHeight;
      const heightValidations = heightQuestion.validations as ValidationsHeight;

      if (heightValidations?.feetMin !== undefined && heightValidations?.feetMin !== null) {
        heightSchema = heightSchema.min(
          heightValidations.feetMin as number,
          `Minimum value is ${heightValidations.feetMin}`,
        );
      }
      if (heightValidations?.feetMax !== undefined && heightValidations?.feetMax !== null) {
        heightSchema = heightSchema.max(
          heightValidations.feetMax as number,
          `Maximum value is ${heightValidations.feetMax}`,
        );
      }

      if (!required) {
        baseSchema = z.union([heightSchema, z.undefined()]);
      } else {
        baseSchema = heightSchema;
      }
      break;
    }

    case 'weight': {
      let weightSchema = z.number({
        required_error: 'Please enter a valid weight',
        invalid_type_error: 'Please enter a valid weight',
      });

      const weightQuestion = question as QuestionWeight;
      const weightValidations = weightQuestion.validations as ValidationsWeight;

      if (weightValidations?.min !== undefined && weightValidations?.min !== null) {
        weightSchema = weightSchema.min(weightValidations.min as number, `Minimum value is ${weightValidations.min}`);
      }
      if (weightValidations?.max !== undefined && weightValidations?.max !== null) {
        weightSchema = weightSchema.max(weightValidations.max as number, `Maximum value is ${weightValidations.max}`);
      }

      if (!required) {
        baseSchema = z.union([weightSchema, z.undefined()]);
      } else {
        baseSchema = weightSchema;
      }
      break;
    }

    case 'height-weight': {
      baseSchema = HeightWeightSchema.refine(
        (data) => {
          const weight = Number(data.weight);
          const height = Number(data.height);
          return weight >= 50 && weight <= 500 && height >= 4 && height <= 8;
        },
        {
          message: 'Weight must be between 50-500 lbs and height between 4-8 feet',
        },
      );
      break;
    }

    case 'exercise-activity-matrix': {
      // Schema for activity matrix - record of activity IDs to activity values
      baseSchema = z.record(z.string(), ActivityValueSchema).default({});
      break;
    }

    case 'name-email': {
      // Schema for name-email questions
      baseSchema = NameEmailSchema;
      break;
    }

    default: {
      // Fallback for unknown question types
      baseSchema = z.any();
      break;
    }
  }

  // Apply required/optional logic
  if (required) {
    return baseSchema.refine(
      (val) => {
        if (val === undefined || val === null) return false;
        if (typeof val === 'string' && val.trim() === '') return false;
        if (Array.isArray(val) && val.length === 0) return false;
        return true;
      },
      { message: 'This field is required' },
    );
  } else {
    return baseSchema.optional();
  }
}

/**
 * Creates a complete Zod schema for the entire questionnaire
 */
export function createQuestionnaireSchema(questionnaire: Questionnaire): z.ZodObject<any> {
  const schemaFields: Record<string, z.ZodSchema> = {};

  // Extract all questions from categories
  const allQuestions: Question[] = questionnaire.categories.flatMap((category: QuestionnaireCategory) =>
    category.items.filter((item): item is Question => isQuestion(item)),
  );

  // Create schema field for each question
  allQuestions.forEach((question) => {
    schemaFields[question.slug] = createQuestionSchema(question);
  });

  return z.object(schemaFields);
}

/**
 * Example usage with default values based on question types
 */
export function createDefaultValues(questionnaire: Questionnaire): QuestionnaireFormData {
  const defaultValues: QuestionnaireFormData = {};

  const allQuestions: Question[] = questionnaire.categories.flatMap((category: QuestionnaireCategory) =>
    category.items.filter((item): item is Question => isQuestion(item)),
  );

  allQuestions.forEach((question) => {
    switch (question.questionType) {
      case 'text':
        defaultValues[question.slug] = '';
        break;
      case 'number':
      case 'number-stepper':
      case 'decimal': {
        const numericQuestion = question as QuestionNumeric | QuestionNumericStepper | QuestionDecimal;
        if (numericQuestion.defaultValue && numericQuestion.defaultValue >= 0) {
          defaultValues[question.slug] = numericQuestion.defaultValue;
        } else {
          defaultValues[question.slug] = 0;
        }
        break;
      }
      case 'date':
        defaultValues[question.slug] = '';
        break;
      case 'select':
      case 'radio':
        defaultValues[question.slug] = '';
        break;
      case 'multiselect':
        defaultValues[question.slug] = [];
        break;
      case 'height':
        defaultValues[question.slug] = 5.5;
        break;
      case 'weight':
        defaultValues[question.slug] = 130;
        break;
      case 'height-weight':
        defaultValues[question.slug] = { weight: '130', height: '5.5' };
        break;
      case 'exercise-activity-matrix': {
        // Default to empty object - component will handle proper defaults based on question config
        defaultValues[question.slug] = {};
        break;
      }
      case 'name-email':
        defaultValues[question.slug] = { name: '', email: '' };
        break;
      default:
        defaultValues[question.slug] = null;
        break;
    }
  });

  return defaultValues;
}

/**
 * Helper function to get validation error messages in a consistent format
 */
export function formatValidationErrors(errors: FieldErrors): Record<string, string> {
  if (!errors || Object.keys(errors).length === 0) {
    return {};
  }
  const formattedErrors: Record<string, string> = {};

  Object.entries(errors).forEach(([key, error]) => {
    formattedErrors[key] = error?.message as string;
  });

  return formattedErrors;
}

export const isQuestion = (item: QuestionnaireCategoryItemsItem): item is Question => {
  return item.questionnaireItemKind === 'question';
};

export const isInterstitial = (item: QuestionnaireCategoryItemsItemOneOfTwo): item is Interstitial => {
  return item.questionnaireItemKind === 'interstitial';
};

/**
 * Example schema for type inference
 */
export type ExampleQuestionnaireFormData = z.infer<ReturnType<typeof createQuestionnaireSchema>>;
